import { useEffect, useRef } from "react";
import { haptic } from "@karrtopelka/game-kit";
import { ROUND_MS, useAimStore } from "../store.js";

/**
 * The play area. One target at a time; tap it to score a hit (it respawns elsewhere).
 * Tapping empty space does nothing — it just wastes time. The 20s countdown timer lives
 * here; when it hits 0 the round ends.
 */
export function Board() {
  const phase = useAimStore((s) => s.phase);
  const target = useAimStore((s) => s.target);
  const hit = useAimStore((s) => s.hit);
  const tick = useAimStore((s) => s.tick);
  const finish = useAimStore((s) => s.finish);

  const endAt = useRef<number | null>(null);
  const raf = useRef<number | null>(null);

  // Drive the countdown while running.
  useEffect(() => {
    if (phase !== "running") return;
    endAt.current = performance.now() + ROUND_MS;
    const loop = () => {
      const left = (endAt.current ?? 0) - performance.now();
      if (left <= 0) {
        tick(0);
        finish();
        return;
      }
      tick(left);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [phase, tick, finish]);

  const onTargetDown = (e: React.PointerEvent) => {
    e.stopPropagation(); // don't let the surface swallow it
    haptic.tap();
    hit();
  };

  return (
    // Width cap lives on the Play wrapper (max-w-md, lg:max-w-xl) so the board can
    // grow on desktop while staying square — the 16% target stays circular.
    <div className="relative aspect-square w-full cursor-crosshair touch-none select-none overflow-hidden rounded-lg border border-border bg-surface shadow-[inset_0_0_80px_rgba(255,111,165,0.04)]">
      {target && (
        // Outer wrapper owns the centering translate so the inner pop animation (which
        // sets `transform: scale`) can't clobber it — otherwise the target jumps off-
        // center during the pop. key on the wrapper retriggers the pop on each spawn.
        <div
          key={target.id}
          className="absolute"
          style={{
            left: `${target.x * 100}%`,
            top: `${target.y * 100}%`,
            width: "16%",
            height: "16%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <button
            type="button"
            onPointerDown={onTargetDown}
            aria-label="target"
            className="animate-pop h-full w-full rounded-full bg-accent shadow-glow hover:brightness-110 active:scale-90"
          >
            <span className="absolute inset-[28%] rounded-full bg-bg/30" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
